export const CURRENCIES = [
  // Major reserve / global
  { code: "USD", symbol: "$",  label: "US Dollar" },
  { code: "EUR", symbol: "€",  label: "Euro" },
  { code: "GBP", symbol: "£",  label: "British Pound" },
  { code: "INR", symbol: "₹",  label: "Indian Rupee" },
  { code: "JPY", symbol: "¥",  label: "Japanese Yen" },
  { code: "CAD", symbol: "C$", label: "Canadian Dollar" },
  { code: "AUD", symbol: "A$", label: "Australian Dollar" },
  { code: "SGD", symbol: "S$", label: "Singapore Dollar" },
  { code: "CHF", symbol: "Fr", label: "Swiss Franc" },
  { code: "CNY", symbol: "¥",  label: "Chinese Yuan" },
  { code: "BRL", symbol: "R$", label: "Brazilian Real" },
  { code: "ZAR", symbol: "R",  label: "South African Rand" },
  { code: "MXN", symbol: "MX$",label: "Mexican Peso" },
  { code: "NZD", symbol: "NZ$",label: "New Zealand Dollar" },

  // Arab League currencies (alphabetised by country)
  { code: "DZD", symbol: "د.ج", label: "Algerian Dinar" },
  { code: "BHD", symbol: ".د.ب", label: "Bahraini Dinar" },
  { code: "KMF", symbol: "CF", label: "Comorian Franc" },
  { code: "DJF", symbol: "Fdj", label: "Djiboutian Franc" },
  { code: "EGP", symbol: "E£", label: "Egyptian Pound" },
  { code: "IQD", symbol: "ع.د", label: "Iraqi Dinar" },
  { code: "JOD", symbol: "د.ا", label: "Jordanian Dinar" },
  { code: "KWD", symbol: "د.ك", label: "Kuwaiti Dinar" },
  { code: "LBP", symbol: "ل.ل", label: "Lebanese Pound" },
  { code: "LYD", symbol: "ل.د", label: "Libyan Dinar" },
  { code: "MRU", symbol: "UM", label: "Mauritanian Ouguiya" },
  { code: "MAD", symbol: "د.م.", label: "Moroccan Dirham" },
  { code: "OMR", symbol: "ر.ع.", label: "Omani Rial" },
  { code: "QAR", symbol: "ر.ق", label: "Qatari Riyal" },
  { code: "SAR", symbol: "ر.س", label: "Saudi Riyal" },
  { code: "SOS", symbol: "Sh", label: "Somali Shilling" },
  { code: "SDG", symbol: "ج.س.", label: "Sudanese Pound" },
  { code: "SYP", symbol: "ل.س", label: "Syrian Pound" },
  { code: "TND", symbol: "د.ت", label: "Tunisian Dinar" },
  { code: "AED", symbol: "د.إ", label: "UAE Dirham" },
  { code: "YER", symbol: "﷼",  label: "Yemeni Rial" },
];

export const currencySymbol = (code) =>
  (CURRENCIES.find(c => c.code === code) || CURRENCIES[0]).symbol;

/** Currencies whose home-locale reads right-to-left (Arabic script). */
export const RTL_CURRENCIES = new Set([
  "DZD", "BHD", "EGP", "IQD", "JOD", "KWD", "LBP", "LYD",
  "MAD", "OMR", "QAR", "SAR", "SDG", "SYP", "TND", "AED", "YER",
]);

export const isRtlCurrency = (code) => RTL_CURRENCIES.has(code);

/**
 * Locale hint for number formatting. We read the current UI language
 * from the `<html lang>` attribute set by LanguageContext (fast, no
 * hook needed).
 *
 * By default we return the language tag as-is so `toLocaleString`
 * uses ASCII digits (matching PayTabs / Mada receipts and most Gulf
 * finance apps). Users who prefer native Eastern-Arabic numerals
 * (٠١٢٣٤٥٦٧٨٩) can opt in via Settings — we then append the
 * `-u-nu-arab` Unicode extension so Intl formats digits natively.
 * The preference is stored in `localStorage.pfo_native_numerals`
 * so it survives reloads without needing a server round-trip.
 */
const _formatLocale = () => {
  if (typeof document === "undefined") return undefined;
  const lang = document.documentElement?.lang || "en";
  try {
    if (typeof window !== "undefined" && window.localStorage?.getItem("pfo_native_numerals") === "1") {
      if (lang.startsWith("ar") || lang.startsWith("ur")) return `${lang}-u-nu-arab`;
      if (lang.startsWith("hi")) return `${lang}-u-nu-deva`;
    }
  } catch (err) {
    // localStorage may be unavailable in sandboxed contexts (Safari
    // private mode, some CSP setups). Fall through to the plain lang.
    console.warn("pfo_native_numerals lookup failed", err);
  }
  return lang;
};

export const formatMoney = (amount, code = "USD") => {
  const isRtl = RTL_CURRENCIES.has(code);
  const docDir = typeof document !== "undefined" ? document.documentElement?.dir : "ltr";
  const n = Number(amount || 0);
  const abs = Math.abs(n);
  const locale = _formatLocale();
  const formatted = abs.toLocaleString(locale, {
    minimumFractionDigits: abs < 1000 ? 2 : 0,
    maximumFractionDigits: 2,
  });
  // For Arabic-script currency symbols embedded inside an LTR layout the
  // browser's bidi algorithm interleaves the symbol with adjacent digits,
  // producing visual glitches like "ر.ع.0.00" → "0.00ر.ع.". Two safeguards:
  //   1) In LTR contexts, fall back to the ISO code (e.g. "OMR 3.90")
  //   2) Always wrap the string in Left-to-Right Marks so it renders as
  //      a single LTR run when embedded inside RTL text as well.
  const sym = (isRtl && docDir !== "rtl") ? `${code} ` : currencySymbol(code);
  const raw = `${n < 0 ? "-" : ""}${sym}${formatted}`;
  return `\u200E${raw}\u200E`;
};

/**
 * formatNumber — locale-aware bare-number formatting (no currency).
 * Use for counts, percentages, quantities. Percentage / decimals still
 * work in RTL contexts because we let Intl handle grouping + digit
 * shaping per the current UI language.
 */
export const formatNumber = (value, opts = {}) => {
  const n = Number(value || 0);
  const locale = _formatLocale();
  return n.toLocaleString(locale, {
    minimumFractionDigits: opts.minDecimals ?? 0,
    maximumFractionDigits: opts.maxDecimals ?? 2,
    ...opts,
  });
};

/**
 * formatDate — short date localised to the current UI language.
 * Falls back to browser default if `document` isn't available (SSR).
 */
export const formatDate = (isoOrDate, opts = { year: "numeric", month: "short", day: "numeric" }) => {
  if (!isoOrDate) return "";
  const d = typeof isoOrDate === "string" ? new Date(isoOrDate) : isoOrDate;
  if (Number.isNaN(d?.getTime?.())) return "";
  const locale = _formatLocale();
  try {
    return d.toLocaleDateString(locale, opts);
  } catch {
    return d.toLocaleDateString(undefined, opts);
  }
};

/**
 * formatYearMonth — renders "YYYY-MM" strings (as returned by the API)
 * as localised "Month YYYY" text. Used by Budget / Reports headers so
 * "2026-02" reads as "February 2026" in English, "فبراير 2026" in
 * Arabic, "फ़रवरी 2026" in Hindi, "فروری 2026" in Urdu.
 */
export const formatYearMonth = (ym) => {
  if (!ym || typeof ym !== "string") return "";
  const [y, m] = ym.split("-").map(Number);
  if (!y || !m) return ym;
  const d = new Date(Date.UTC(y, m - 1, 1));
  const locale = _formatLocale();
  try {
    return d.toLocaleDateString(locale, { year: "numeric", month: "long" });
  } catch {
    return d.toLocaleDateString(undefined, { year: "numeric", month: "long" });
  }
};

/**
 * formatMonthShort — short month name (localised). Used inline in
 * streak heat strips and dashboard sub-headers.
 */
export const formatMonthShort = (ym) => {
  if (!ym) return "";
  const [y, m] = ym.split("-").map(Number);
  if (!y || !m) return ym;
  const d = new Date(Date.UTC(y, m - 1, 1));
  const locale = _formatLocale();
  try {
    return d.toLocaleDateString(locale, { month: "short" });
  } catch {
    return d.toLocaleDateString(undefined, { month: "short" });
  }
};
